/* eslint complexity: ["error", {max: 24}] -- web/native branching across multiple props */
/* eslint max-lines-per-function: ["error", {max: 200}] -- Complex layout component with multiple handlers */
/* eslint max-lines: ["error", { max: 315 }] -- Complex layout component with multiple handlers */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { JSX, PropsWithChildren } from "react";

import {
  BottomSheetBackdrop,
  BottomSheetView,
  BottomSheetModal as GorhomBottomSheetModal,
} from "@gorhom/bottom-sheet";
import type {
  BottomSheetBackdropProps,
  BottomSheetProps,
} from "@gorhom/bottom-sheet";
import { Keyboard, useWindowDimensions } from "react-native";
import { FullWindowOverlay } from "react-native-screens";
import { Provider as ReduxProvider } from "react-redux";
import type { ViewProps } from "tamagui";

import { ModalTestID } from "@/constants/TestID";
import store from "@/store";
import BottomSheetModalContainer from "@/ui/composed/modal/BottomSheetModal/BottomSheetModalContentContainer";
import BottomSheetTitleHeader from "@/ui/composed/modal/BottomSheetModal/BottomSheetTitleHeader";
import ToastProvider from "@/ui/composed/toast/ToastProvider";
import { KeyboardDoneToolbar } from "@/ui/core";
import { Box } from "@/ui/layout";
import { WebContentMaxWidth } from "@/ui/layout/ScreenContainer";
import { ShadowStyles } from "@/ui/theme/styles";
import { isAndroid, isWeb } from "@/utils/platform.util";

const DefaultSnapPoints: BottomSheetProps["snapPoints"] = ["80%"];

// Defined outside the component so the reference is stable and gorhom does not remount
const ToolbarContainer = () => (
  <Box bottom={0} pointerEvents="box-none" position="absolute" width="100%">
    <KeyboardDoneToolbar />
  </Box>
);

const WebContainerStyle = {
  bottom: 0,
  // 100dvh tracks the *visible* viewport (shrinks when Safari's toolbars show), so the sheet's bottom edge lands above the toolbar instead of behind it.
  // top:0/bottom:0 + would resolve to the large viewport (lvh) and hide the bottom CTAs on mobile Safari.
  height: "100dvh",
  left: 0,
  position: "fixed" as const,
  right: 0,
};

const createContainerComponent = (shouldRenderToolbar: boolean = true) => {
  const ContainerComponent = ({ children }: PropsWithChildren) => {
    const toolbar = shouldRenderToolbar && <ToolbarContainer />;
    const content = (
      <>
        {children}
        {toolbar}
      </>
    );

    // On iOS, use FullWindowOverlay to render above modal screens
    // But we need to manually bridge context (Redux, Toast) through the portal
    // https://github.com/gorhom/react-native-bottom-sheet/issues/832
    if (isWeb()) {
      return (
        <Box
          pointerEvents="box-none"
          style={WebContainerStyle}
          testID={ModalTestID.WebBottomSheetContainer}
        >
          {content}
        </Box>
      );
    }
    if (isAndroid()) return content;

    // On iOS, use FullWindowOverlay to render above modal screens
    // But we need to manually bridge context (Redux, Toast) through the portal

    return (
      <FullWindowOverlay>
        <ReduxProvider store={store}>
          <ToastProvider>{content}</ToastProvider>
        </ReduxProvider>
      </FullWindowOverlay>
    );
  };

  return ContainerComponent;
};

interface BottomSheetModalProps extends PropsWithChildren<ViewWithTestID> {
  contentContainerGap?: ViewProps["gap"];
  dismissAccessibilityLabel: string;
  FABComponent?: JSX.Element;
  icon?: JSX.Element;
  isCloseOnOverlayPressDisabled?: boolean;
  isOpen: boolean;
  isScrollable?: boolean;
  isSmallBottomPadding?: boolean;
  isSwipeDownToCloseDisabled?: boolean;
  onDismissAnimationEnd?: () => void;
  onRequestClose: () => void;
  shouldRenderFloatingButton?: boolean;
  shouldRenderKeyboardToolbar?: boolean;
  shouldResizeOnContentChange?: boolean;
  snapPoints?: BottomSheetProps["snapPoints"];
  title?: string;
}

// eslint-disable-next-line no-restricted-syntax -- This component requires multiple props to accommodate various use cases
const BottomSheetModal = ({
  children,
  contentContainerGap,
  dismissAccessibilityLabel,
  FABComponent,
  icon,
  isCloseOnOverlayPressDisabled,
  isOpen,
  isScrollable,
  isSmallBottomPadding,
  isSwipeDownToCloseDisabled,
  onDismissAnimationEnd,
  onRequestClose,
  shouldRenderFloatingButton,
  shouldRenderKeyboardToolbar = true,
  shouldResizeOnContentChange,
  snapPoints = DefaultSnapPoints,
  testID,
  title,
}: BottomSheetModalProps) => {
  const ref = useRef<GorhomBottomSheetModal>(null);
  const { height: windowHeight } = useWindowDimensions();
  // Track if modal has ever been opened to prevent early render issues
  const [hasBeenOpened, setHasBeenOpened] = useState(false);
  // True once gorhom has dismissed itself (swipe/backdrop) so the close effect doesn't
  // call a redundant dismiss(). Set in handleDismiss; reset on each open (not on close) —
  // handleDismiss fires AFTER the close effect, so resetting on close would leave it
  // stale-true and break the next programmatic close.
  const isDismissingRef = useRef(false);
  // Holds the latest isOpen so handleDismiss can tell who closed the sheet. On a
  // gesture/backdrop close the parent hasn't reacted yet, so isOpen is still true and we
  // call onRequestClose to let it catch up. On a programmatic close the parent already
  // set isOpen to false, so we skip onRequestClose — calling it again would re-run the
  // parent's close handler (e.g. SMT resetSMTState, which unmounts this modal for support
  // users). handleDismiss runs at the end of the close animation rather than the start,
  // because reopening mid-close cancels it: onDismiss never fires, so a fast re-tap to
  // reopen isn't undone.
  const isOpenRef = useRef(isOpen);
  isOpenRef.current = isOpen;

  // on web, only used in scrollable mode - gives the sheet a definitive pixel height so that the inner list can scroll
  // must be a specific pixel #, not a "%" string; using a "%" causes the gorhom sheet to be unable to scroll
  const firstSnapPoint =
    isScrollable && isWeb() && Array.isArray(snapPoints)
      ? snapPoints[0]
      : undefined;
  const webMaxHeight =
    typeof firstSnapPoint === "string" && firstSnapPoint.endsWith("%")
      ? windowHeight * (Number.parseFloat(firstSnapPoint) / 100)
      : firstSnapPoint;

  const renderContainerComponent = useMemo(
    () => createContainerComponent(shouldRenderKeyboardToolbar),
    [shouldRenderKeyboardToolbar],
  );

  useEffect(() => {
    if (!isOpen) {
      // Only dismiss programmatically if gorhom hasn't already dismissed itself via a
      // gesture/backdrop. Calling dismiss() again after gorhom unmounted corrupts the
      // portal state and the sheet won't reopen.
      if (!isDismissingRef.current) {
        ref.current?.dismiss();
      }
      return;
    }

    // Fresh open — reset the gesture-dismiss flag for this open/close cycle.
    isDismissingRef.current = false;
    Keyboard.dismiss();
    ref.current?.present();
    setHasBeenOpened(true);
  }, [isOpen]);

  useEffect(() => {
    // The OS shifts the window up when the keyboard appears but gorhom doesn't animate back down on dismiss.
    // The issue is still open with gorhom: https://github.com/gorhom/react-native-bottom-sheet/issues/2509
    // keyboardWillHide fires before the animation on iOS (feels instant).
    // keyboardDidHide is used on Android since keyboardWillHide is iOS-only.
    const event = isAndroid() ? "keyboardDidHide" : "keyboardWillHide";
    const subscription = Keyboard.addListener(event, () => {
      ref.current?.snapToIndex(0);
    });
    return () => subscription.remove();
  }, []);

  // Fires after gorhom fully unmounts the sheet (swipe/backdrop dismiss, or a programmatic
  // dismiss()). Mark that gorhom dismissed itself so the close effect won't call a
  // redundant dismiss(). Only echo onRequestClose back to the parent for a USER-initiated
  // close (isOpenRef still true) — on a programmatic close the parent already set
  // isOpen=false, and echoing it re-fires parent side effects (e.g. SMT resetSMTState,
  // which unmounts this modal for support users and is the "replace photo" bug).
  const handleDismiss = useCallback(() => {
    isDismissingRef.current = true;
    if (isOpenRef.current) {
      onRequestClose();
    }
    onDismissAnimationEnd?.();
  }, [onRequestClose, onDismissAnimationEnd]);

  const renderBackdrop = useCallback(
    (props: BottomSheetBackdropProps) => (
      <BottomSheetBackdrop
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...props}
        appearsOnIndex={0}
        disappearsOnIndex={-1}
        opacity={0.2}
        pressBehavior={isCloseOnOverlayPressDisabled ? "none" : "close"}
      />
    ),
    [isCloseOnOverlayPressDisabled],
  );

  // CRITICAL: Don't render until first open - @gorhom/bottom-sheet v5 breaks if mounted while closed
  // Keep mounted after first open to preserve React context for nested components
  // See: docs/expo-upgrades/expo-55-upgrade.md (section 11)
  if (!isOpen && !hasBeenOpened) {
    return null;
  }

  return (
    <GorhomBottomSheetModal
      backdropComponent={renderBackdrop}
      backgroundStyle={{
        backgroundColor: "transparent",
        // on web, disable the thin square border around the correctly-rounded corners
        ...(isWeb() && { borderRadius: 0 }),
      }}
      containerComponent={renderContainerComponent}
      enableDynamicSizing={isWeb() || !isScrollable}
      // Disables a gesture that allows the user to "drag up" the bottom sheet
      enableOverDrag={false}
      enablePanDownToClose={!isSwipeDownToCloseDisabled}
      handleComponent={null}
      maxDynamicContentSize={
        isWeb() || !isScrollable ? windowHeight * 0.8 : undefined
      }
      onDismiss={handleDismiss}
      ref={ref}
      snapPoints={isScrollable && !isWeb() ? snapPoints : undefined}
      stackBehavior="push"
      style={[
        !isWeb() && ShadowStyles,
        isWeb() && {
          marginHorizontal: "auto",
          maxWidth: WebContentMaxWidth,
          width: "100%",
        },
      ]}
    >
      {isScrollable && !isWeb() ? (
        <BottomSheetModalContainer
          gap={contentContainerGap}
          isScrollable
          isSmallBottomPadding={isSmallBottomPadding}
          shouldResizeOnContentChange={shouldResizeOnContentChange}
          testID={testID}
        >
          {title && (
            <BottomSheetTitleHeader
              dismissAccessibilityLabel={dismissAccessibilityLabel}
              icon={icon}
              onDismiss={onRequestClose}
              testID={testID}
              title={title}
            />
          )}
          {children}
          {shouldRenderFloatingButton && FABComponent}
        </BottomSheetModalContainer>
      ) : (
        <BottomSheetView>
          <BottomSheetModalContainer
            gap={contentContainerGap}
            isScrollable={isScrollable}
            isSmallBottomPadding={isSmallBottomPadding}
            maxHeight={webMaxHeight}
            shouldResizeOnContentChange={shouldResizeOnContentChange}
            testID={testID}
          >
            {title && (
              <BottomSheetTitleHeader
                dismissAccessibilityLabel={dismissAccessibilityLabel}
                icon={icon}
                onDismiss={onRequestClose}
                testID={testID}
                title={title}
              />
            )}
            {children}
          </BottomSheetModalContainer>
          {shouldRenderFloatingButton && FABComponent}
        </BottomSheetView>
      )}
    </GorhomBottomSheetModal>
  );
};

export type { BottomSheetModalProps };
export default BottomSheetModal;
